import { useEffect } from 'react';
import PropTypes from 'prop-types';
import { useRouteMatch } from 'react-router-dom';
import { FormattedMessage, useIntl } from 'react-intl';

import { Helmet } from 'react-helmet';
import { Button, Divider, Form, Modal, Space } from 'antd';

import { useBreadcrumbs } from '@/contexts/BreadcrumbContextProvider';

import { InsurerContact } from '@/core/domain/people/InsurerContact/InsurersContact.types';
import {
  DefaultRouteTabs,
  DeleteIcon,
  EditIcon,
  InfiniteTable,
  MultipleContactModal,
  SaveButton,
} from '@/components/_common';
import { useDisclosure } from '@/hooks';
import {
  useInsurer,
  useInsurerContactCreate,
  useInsurerContactDetailRemove,
  useInsurerContactEdit,
  useInsurerContactList,
  useInsurerContactRemove,
} from '@/core/domain/people/InsurerContact/InsurersContact.hooks';
import { PageProps } from '@/types/PageProps';
import { LegalPersonContactFormInterface } from '@/components/_common/MultipleContactModal/types';
import { COLUMNS_WIDTH, getTableScrollX } from '@/utils/table';
import { ColumnsType } from 'antd/lib/table';
import moment from 'moment';
import { useInsurerFormContext } from '@/components/Insurers/InsurerFormProvider';
import { DateTimeFormat } from '@/utils/moment';
import SpaceForButton from '@/components/_common/SpaceForButtons';
import InsurerFooter from '@/components/Insurers/InsurerFooter';
import { hasPermissions } from '@/utils/functions/hasPermissions';
import Authorities from '@/types/Authority';
import { removeMaskDetailList } from '@/utils/mask/removeMaskDetailList';
import { ContactDetailList } from '@/core/domain/people/_shared/Contact';
import { useContactColumns } from '@/hooks/useContactColumns';

const InsurerContactsPage = ({ route }: PageProps) => {
  const intl = useIntl();
  const [form] = Form.useForm<
    LegalPersonContactFormInterface & { detailList: ContactDetailList[] }
  >();
  const [formSubmit] = Form.useForm();
  const { insurerId } = useRouteMatch<{ insurerId: string }>().params;
  const [modalVisible, modalActions] = useDisclosure(false);
  const { setParamLabel } = useBreadcrumbs();
  const { columns: columnsContact } = useContactColumns<InsurerContact>();

  const insurers = useInsurer(Number(insurerId));
  const insurerContacts = useInsurerContactList(Number(insurerId));
  const insurerContactCreate = useInsurerContactCreate();
  const insurerContactEdit = useInsurerContactEdit();
  const insurerContactRemove = useInsurerContactRemove();
  const insurerContactDetailRemove = useInsurerContactDetailRemove();

  const { isLoading, disabled } = useInsurerFormContext();

  useEffect(() => {
    if (insurers.data) {
      setParamLabel(':insurerId', insurers.data.name);
    }
  }, [insurers.data]);

  useEffect(() => {
    if (insurerContacts.data?.length && modalVisible) {
      const contactUpdated = insurerContacts.data.find(
        (contact) => contact.id === form.getFieldValue('contactId'),
      );

      if (contactUpdated) {
        handleContactSelect(contactUpdated);
      }
    }
  }, [insurerContacts.data, modalVisible]);

  async function handleSave() {
    try {
      const { contactId, ...values } = await form.validateFields();

      const detailList = removeMaskDetailList(values.detailList);

      if (contactId) {
        await insurerContactEdit.mutateAsync({
          ...values,
          id: contactId,
          insurerId: Number(insurerId),
          birthDate: values.birthDate
            ? values.birthDate.format(DateTimeFormat)
            : undefined,
          detailList,
        });
      } else {
        await insurerContactCreate.mutateAsync({
          value: {
            ...values,
            insurerId: Number(insurerId),
            birthDate: values.birthDate
              ? values.birthDate.format(DateTimeFormat)
              : undefined,
            detailList,
          },
        });
      }

      modalActions.close();
    } catch {}
  }

  function handleContactRemove(insurerContact: InsurerContact) {
    Modal.confirm({
      title: intl.formatMessage({
        id: 'customers.contact.remove',
      }),
      content: `${intl.formatMessage({
        id: 'customers.contact.remove.alert',
      })} ${insurerContact?.name}?`,
      onOk: () =>
        insurerContactRemove.mutateAsync({
          insurerId: insurerContact.insurerId,
          contactId: insurerContact.id,
        }),
      okText: intl.formatMessage({ id: 'commons.form.button.remove' }),
    });
  }

  async function handleRemoveContactDetail(detailId: number) {
    await insurerContactDetailRemove.mutateAsync({
      insurerId: Number(insurerId),
      detailId,
    });

    insurerContacts.refetch();
  }

  function handleContactSelect(insurerContact: InsurerContact) {
    form.setFieldsValue({
      ...insurerContact,
      detailList: insurerContact.detailList,
      contactId: insurerContact.id,
      birthDate: insurerContact.birthDate
        ? moment(insurerContact.birthDate)
        : undefined,
    });
  }

  const columns: ColumnsType<InsurerContact> = [
    ...columnsContact,
    {
      key: 'action',
      title: intl.formatMessage({ id: 'commons.list.header.action' }),
      align: 'right',
      fixed: 'right',
      width: COLUMNS_WIDTH.small,
      render: (_, row: InsurerContact) => (
        <Space>
          <EditIcon
            iconProps={{
              onClick: () => {
                handleContactSelect(row);
                modalActions.open();
              },
            }}
            disabled={!hasPermissions([Authorities.REGISTER_INSURER_EDIT])}
          />

          <Divider type="vertical" />

          <DeleteIcon
            onClick={() => handleContactRemove(row)}
            disabled={!hasPermissions([Authorities.REGISTER_INSURER_EDIT])}
          />
        </Space>
      ),
    },
  ];

  return (
    <DefaultRouteTabs
      permissions={route.permissions}
      route={route.parent}
      baseKey={insurerId}
      basePath={`/app/registry/insurers/${insurerId}`}
      extra={
        <SpaceForButton>
          <Button
            block
            type="primary"
            onClick={modalActions.open}
            disabled={!hasPermissions([Authorities.REGISTER_INSURER_EDIT])}
          >
            <FormattedMessage id="commons.entity.add" />
          </Button>

          <SaveButton
            block
            type="primary"
            onClick={formSubmit.submit}
            loading={isLoading}
            disabled={
              disabled || !hasPermissions([Authorities.REGISTER_INSURER_EDIT])
            }
          >
            {intl.formatMessage({
              id: 'layout.menu.registry.insurers.saveText',
            })}
          </SaveButton>
        </SpaceForButton>
      }
    >
      <Helmet
        title={intl.formatMessage({
          id: route.name,
        })}
      />

      <Form form={formSubmit} name="insurerContacts" />

      <InfiniteTable
        dataSource={insurerContacts.data}
        rowKey={(row) => row.id}
        columns={columns}
        loading={insurerContacts.isLoading}
        pagination={false}
        scroll={{ x: getTableScrollX(columns) }}
      />

      <InsurerFooter disabled={disabled} />

      <MultipleContactModal
        open={modalVisible}
        onCancel={modalActions.close}
        onOk={handleSave}
        confirmLoading={
          insurerContactCreate.isLoading || insurerContactEdit.isLoading
        }
        form={form}
        personType="NaturalPerson"
        onDetailRemove={handleRemoveContactDetail}
        loading={insurerContactCreate.isLoading || insurerContactEdit.isLoading}
      />
    </DefaultRouteTabs>
  );
};

InsurerContactsPage.propTypes = {
  route: PropTypes.object.isRequired,
};

export default InsurerContactsPage;
